import { gql } from "@apollo/client";
import { API } from "api/API";
import axiosInstance from "api/axiosInstance";
import { AxiosProgressEvent } from "axios";

const APIInstance = new API();

export const insertActionPlan = gql`
    mutation insertActionPlan($action_plan: action_plan_insert_input!) {
        insert_action_plan_one(object: $action_plan) {
            action_plan_id
        }
    }
`;

export const getAllActionPlansCount = gql`
    query getAllActionPlansCount($where: action_plan_bool_exp) {
        action_plan_aggregate(where: $where) {
            aggregate {
                count
            }
        }
    }
`;

export const getAllActionPlans = gql`
    query getAllActionPlans(
        $limit: Int!
        $offset: Int!
        $order_by: [action_plan_order_by!]
        $where: action_plan_bool_exp
    ) {
        action_plan(limit: $limit, offset: $offset, order_by: $order_by, where: $where) {
            action_plan_id
            display_number
            issue_id
            issue {
                display_number
            }
            created_by
            submitted_by_user_id
            assignee_id
            rca_id
            rca {
                display_number
            }
            description
            due_date
            name
            meeting_type_id
            status {
                name
                status_id
                order
                default_status_translations {
                    status_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
            assignee {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
            submitted_by {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
        }
    }
`;

export const getActionPlanHistoricChangesLog = gql`
    query getActionPlanHistoricChangesLog($id: Int) {
        action_plan_history_log(where: { action_plan_id: { _eq: $id } }, order_by: { changed_at: desc }) {
            history_id
            field_name
            new_value
            old_value
            changed_at
            change_type
            user {
                first_name
                last_name
                media {
                    media_url
                }
            }
        }
    }
`;

export const getActionPlan = gql`
    query getActionPlan($action_plan_id: Int!) {
        action_plan(where: { action_plan_id: { _eq: $action_plan_id } }) {
            action_plan_id
            display_number
            issue_id
            created_by
            submitted_by_user_id
            rca_id
            description
            due_date
            meeting_type_id
            name
            rca {
                display_number
                meeting_type_id
                mill_id
            }
            issue {
                display_number
                meeting_type_id
                mill_id
            }
            submitted_by {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
            assignee {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
            approved_on
            rejected_on
            approver {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
            priority {
                name
                priority_id
                default_priority_translations {
                    priority_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
            status {
                name
                status_id
                order
                default_status_translations {
                    status_id
                    language {
                        language_id
                        name
                        code
                    }
                    translated_name
                }
            }
            action_plan_media(where: { media: { is_enabled: { _eq: true } }, deleted_at: { _is_null: true } }) {
                action_plan_media_id
                action_plan_id
                media {
                    name
                    media_id
                    media_type
                    media_url
                    is_enabled
                }
            }
        }
    }
`;

export const updateActionPlan = gql`
    mutation updateActionPlan($actionPlanId: Int!, $changes: action_plan_set_input!) {
        update_action_plan(where: { action_plan_id: { _eq: $actionPlanId } }, _set: $changes) {
            affected_rows
            returning {
                action_plan_id
            }
        }
    }
`;

export const getActionPlanTasks = gql`
    query GetActionPlanTasks($actionPlanId: Int!) {
        action_plan_task(
            where: { deleted_at: { _is_null: true }, action_plan_id: { _eq: $actionPlanId } }
            order_by: { action_plan_task_id: asc }
        ) {
            action_plan_id
            action_plan_task_id
            description
            action_plan {
                action_plan_id
                description
            }
            completed_at
            created_at
            deleted_at
            updated_at
        }
    }
`;

export const insertActionPlanTask = gql`
    mutation insertActionPlanTask($action_plan_task: action_plan_task_insert_input!) {
        insert_action_plan_task_one(object: $action_plan_task) {
            action_plan_task_id
        }
    }
`;

export const updateActionPlanTask = gql`
    mutation updateActionPlanTask($action_plan_task_id: Int!, $changes: action_plan_task_set_input!) {
        update_action_plan_task_by_pk(pk_columns: { action_plan_task_id: $action_plan_task_id }, _set: $changes) {
            action_plan_task_id
        }
    }
`;

export const uploadActionPlanFiles = async (
    actionPlanId: number,
    userImageFormData: FormData,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
): Promise<{ publicUrl: string } | unknown> => {
    try {
        const url = `${APIInstance.api_root}/upload/action-plan-media/${actionPlanId}`;
        const response = await axiosInstance.put<{ publicUrl: string }>(url, userImageFormData, { onUploadProgress });

        return response;
    } catch (error) {
        console.error(error);
        return error;
    }
};

export const updateActionPlanMedia = gql`
    mutation updateActionPlanMedia($actionPlanMediaId: Int!, $changes: action_plan_media_set_input!) {
        update_action_plan_media(where: { action_plan_media_id: { _eq: $actionPlanMediaId } }, _set: $changes) {
            affected_rows
            returning {
                action_plan_media_id
            }
        }
    }
`;

export const getActionPlanCommentsCount = gql`
    query getActionPlanCommentsCount($actionPlanId: Int!) {
        action_plan_comment_aggregate(
            where: { action_plan_id: { _eq: $actionPlanId }, deleted_at: { _is_null: true } }
        ) {
            aggregate {
                count
            }
        }
    }
`;

export const getActionPlanComments = gql`
    query getActionPlanComments(
        $actionPlanId: Int!
        $limit: Int!
        $offset: Int!
        $order_by: [action_plan_comment_order_by!]
    ) {
        action_plan_comment(
            where: { action_plan_id: { _eq: $actionPlanId }, deleted_at: { _is_null: true } }
            limit: $limit
            offset: $offset
            order_by: $order_by
        ) {
            action_plan_comment_id
            action_plan_id
            user_id
            description
            created_at
            updated_at
            user {
                user_id
                first_name
                last_name
                media {
                    media_id
                    media_url
                    media_type
                }
            }
        }
    }
`;

export const insertActionPlanComment = gql`
    mutation insertActionPlanComment($actionPlanComment: action_plan_comment_insert_input!) {
        insert_action_plan_comment_one(object: $actionPlanComment) {
            action_plan_comment_id
        }
    }
`;

export const updateActionPlanComment = gql`
    mutation updateActionPlanComment($actionPlanCommentId: Int!, $changes: action_plan_comment_set_input!) {
        update_action_plan_comment_by_pk(pk_columns: { action_plan_comment_id: $actionPlanCommentId }, _set: $changes) {
            action_plan_comment_id
        }
    }
`;
